import React, { useMemo, useState } from "react";
import {
  Accordion,
  Badge,
  Button,
  Card,
  Col,
  Form,
  FloatingLabel,
  Modal,
  Nav,
  Row,
  Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { API_ENDPOINTS } from "../config/api";
import UserAvatar from "./UserAvatar";
import ExpandableUserSelector from "./ExpandableUserSelector";
import { CalendarFold, CalendarX } from "lucide-react";

/**
 * HTML date inputs yield "YYYY-MM-DD". Sending that string to the API makes
 * `new Date("YYYY-MM-DD")` UTC midnight on that calendar day, so the end date
 * is effectively the start of the chosen day in UTC — delegations look
 * "expired" for most of the delegate's local end day. Convert to inclusive
 * local-day instants before JSON serialization.
 */
function inclusiveLocalRangeFromDateInputs(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const [sy, sm, sd] = startDate.split("-").map(Number);
  const [ey, em, ed] = endDate.split("-").map(Number);
  if ([sy, sm, sd, ey, em, ed].some((n) => Number.isNaN(n))) return null;
  const start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
  const end = new Date(ey, em - 1, ed, 23, 59, 59, 999);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return { start, end };
}

const TABLE_COL_COUNT = 7;

function isDelegationDateExpired(d) {
  if (!d?.endDate) return false;
  const end = new Date(d.endDate);
  if (Number.isNaN(end.getTime())) return false;
  return new Date() > end;
}

const MODULE_KEYS = ["onboarding", "conferenceScheduler", "resources"];

const moduleLabel = (moduleKey) => {
  switch (moduleKey) {
    case "onboarding":
      return "Onboarding management";
    case "conferenceScheduler":
      return "Conference scheduling";
    case "resources":
      return "Resources";
    default:
      return moduleKey;
  }
};

const ROLE_FILTER_OPTIONS = [
  { key: "", label: "All roles" },
  { key: "resident", label: "Residents" },
  { key: "attending", label: "Attendings" },
  /*{ key: "medical student", label: "Medical Students" },*/
  { key: "administrative assistant", label: "Administrative Assistants" },
];

const MODULE_FILTER_OPTIONS = [
  { key: "", label: "All tasks" },
  ...MODULE_KEYS.map((key) => ({ key, label: moduleLabel(key) })),
];

export default function DelegationsManager({ users = [] }) {
  const token = localStorage.getItem("token");

  const selectableUsers = useMemo(() => {
    // Director can't delegate to themselves.
    return (users || []).filter((u) => u && u.role !== "director" && u.role !== "delegate");
  }, [users]);

  const [delegations, setDelegations] = useState([]);
  const [loading, setLoading] = useState(false);

  const [roleFilter, setRoleFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    delegateeId: "",
    moduleKey: "onboarding",
    startDate: "",
    endDate: "",
  });

  const refreshDelegations = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_ENDPOINTS.DELEGATIONS}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load delegations");
      const data = await res.json();
      setDelegations(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load delegations");
    } finally {
      setLoading(false);
    }
  };

  // Fetch on first render of this component.
  React.useEffect(() => {
    refreshDelegations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm({ delegateeId: "", moduleKey: "onboarding", startDate: "", endDate: "" });
  };

  const submit = async () => {
    const { delegateeId, moduleKey, startDate, endDate } = form;
    if (!delegateeId) return toast.error("Select a user to delegate to");
    if (!moduleKey) return toast.error("Select a task/module");
    if (!startDate || !endDate) return toast.error("Start and end dates are required");
    const range = inclusiveLocalRangeFromDateInputs(startDate, endDate);
    if (!range) return toast.error("Invalid start or end date");
    if (range.end < range.start) return toast.error("End date must be on or after start date");

    try {
      const res = await fetch(`${API_ENDPOINTS.DELEGATIONS}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delegateeId,
          moduleKey,
          startDate: range.start.toISOString(),
          endDate: range.end.toISOString(),
        }),
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || "Failed to create delegation");
      }

      toast.success("Delegation created");
      setShowModal(false);
      resetForm();
      await refreshDelegations();
    } catch (e) {
      console.error(e);
      toast.error("Failed to create delegation");
    }
  };

  const revoke = async (delegationId) => {
    if (!delegationId) return;
    const ok = window.confirm("Revoke this delegation? The delegate will lose access immediately.");
    if (!ok) return;

    try {
      const res = await fetch(`${API_ENDPOINTS.DELEGATIONS}/${delegationId}/revoke`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to revoke delegation");
      toast.success("Delegation revoked");
      await refreshDelegations();
    } catch (e) {
      console.error(e);
      toast.error("Failed to revoke delegation");
    }
  };

  const permanentDelete = async (delegationId) => {
    if (!delegationId) return;
    const ok = window.confirm(
      "Permanently delete this delegation? This cannot be undone."
    );
    if (!ok) return;

    try {
      const res = await fetch(`${API_ENDPOINTS.DELEGATIONS}/${delegationId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to delete delegation");
      toast.success("Delegation deleted");
      await refreshDelegations();
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete delegation");
    }
  };

  const reinstate = async (delegation) => {
    if (!delegation) return;

    const { id } = delegation;
    if (!id) {
      toast.error("Cannot reinstate: missing delegation id");
      return;
    }

    const ok = window.confirm("Reinstate this delegation for its original date range?");
    if (!ok) return;

    try {
      const res = await fetch(`${API_ENDPOINTS.DELEGATIONS}/${id}/reinstate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || "Failed to reinstate delegation");
      }

      toast.success("Delegation reinstated");
      await refreshDelegations();
    } catch (e) {
      console.error(e);
      toast.error("Failed to reinstate delegation");
    }
  };

  const isDateRangeActive = (startDate, endDate) => {
    try {
      if (!startDate || !endDate) return false;
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
      const now = new Date();
      return now >= start && now <= end;
    } catch {
      return false;
    }
  };

  const getDelegationStatus = (d) => {
    if (d?.revokedAt) return "revoked";
    if (!d?.startDate || !d?.endDate) return "expired";
    const start = new Date(d.startDate);
    const end = new Date(d.endDate);
    const now = new Date();
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "expired";
    if (now > end) return "expired";
    if (now < start) return "pending";
    if (now >= start && now <= end) return "active";
    return "expired";
  };

  const getDelegateeUser = (d) =>
    (users || []).find((x) => String(x.id) === String(d?.delegateeId)) || d?.delegatee || null;

  const filteredDelegations = useMemo(() => {
    return delegations.filter((d) => {
      if (moduleFilter && d.moduleKey !== moduleFilter) return false;
      if (roleFilter) {
        const role = getDelegateeUser(d)?.role;
        if (role !== roleFilter) return false;
      }
      return true;
    });
  }, [delegations, roleFilter, moduleFilter, users]);

  const { activeSectionRows, expiredSectionRows } = useMemo(() => {
    const active = [];
    const expired = [];
    for (const d of filteredDelegations) {
      if (isDelegationDateExpired(d)) expired.push(d);
      else active.push(d);
    }
    return { activeSectionRows: active, expiredSectionRows: expired };
  }, [filteredDelegations]);

  const hasActiveFilters = Boolean(roleFilter || moduleFilter);

  const clearFilters = () => {
    setRoleFilter("");
    setModuleFilter("");
  };

  const getUserLabel = (userId) => {
    const u = (users || []).find((x) => String(x.id) === String(userId));
    if (!u) return `User #${userId}`;
    return `${u.firstName} ${u.lastName} (${u.role})`;
  };

  const statusBadgeVariant = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "pending":
        return "info";
      case "revoked":
        return "secondary";
      default:
        return "warning";
    }
  };

  const renderActions = (d) => {
    const dateExpired = isDelegationDateExpired(d);
    return (
      <div className="d-flex flex-wrap gap-2 align-items-center">
        {!dateExpired && !d.revokedAt ? (
          <Button variant="outline-danger" size="sm" onClick={() => revoke(d.id)}>
            Revoke
          </Button>
        ) : null}
        {!dateExpired && d.revokedAt && isDateRangeActive(d.startDate, d.endDate) ? (
          <Button variant="outline-success" size="sm" onClick={() => reinstate(d)}>
            Reinstate
          </Button>
        ) : null}
        <Button variant="outline-secondary" size="sm" onClick={() => permanentDelete(d.id)}>
          Delete
        </Button>
      </div>
    );
  };

  const renderDelegationRow = (d) => {
    const status = getDelegationStatus(d);
    return (
      <tr key={d.id} className="delegation-data-row">
        <td>
          <div className="d-flex flex-row align-items-center gap-2">
            <UserAvatar user={getDelegateeUser(d)} size={32} alt="" />
            {(() => {
              const label = getUserLabel(d.delegateeId) || "";
              return label.replace(/\s*\([^)]+\)/g, "");
            })()}
          </div>
        </td>
        <td>{getDelegateeUser(d)?.role || ""}</td>
        <td>{moduleLabel(d.moduleKey)}</td>
        <td>{d.startDate ? new Date(d.startDate).toLocaleDateString() : ""}</td>
        <td>{d.endDate ? new Date(d.endDate).toLocaleDateString() : ""}</td>
        <td>
          <Badge bg={statusBadgeVariant(status)}>{status}</Badge>
        </td>
        <td>{renderActions(d)}</td>
      </tr>
    );
  };

  const renderFilterNav = (idPrefix = "") => (
    <Nav variant="pills" className="flex-column">
      <Nav.Item>
        <Nav.Link className="disabled fw-semibold">Filter</Nav.Link>
      </Nav.Item>
        <span className="navbar-text text-muted small fw-semibold px-2 ">By role</span>
      {ROLE_FILTER_OPTIONS.map((opt) => (
        <Nav.Item key={`${idPrefix}role-${opt.key || "all"}`}>
          <Nav.Link
            active={roleFilter === opt.key}
            onClick={() => setRoleFilter(opt.key)}
            className="py-2"
          >
            {opt.label}
          </Nav.Link>
        </Nav.Item>
      ))}
        <span className="navbar-text text-muted small fw-semibold px-2 mt-2">
          By Assigned tasks
        </span>
   
      
      {MODULE_FILTER_OPTIONS.map((opt) => (
        <Nav.Item key={`${idPrefix}module-${opt.key || "all"}`}>
          <Nav.Link
            active={moduleFilter === opt.key}
            onClick={() => setModuleFilter(opt.key)}
            className="py-2"
          >
            {opt.label}
          </Nav.Link>
        </Nav.Item>
      ))}
    </Nav>
  );

  return (
    <div>
      <div className="d-md-none mb-3">
        <Accordion defaultActiveKey={null}>
          <Card className="bg-white shadow-sm border-0">
            <Accordion.Item eventKey="0" className="border-0">
              <Accordion.Header>Filters</Accordion.Header>
              <Accordion.Body className="p-0">{renderFilterNav("m-")}</Accordion.Body>
            </Accordion.Item>
          </Card>
        </Accordion>
      </div>

      <Row>
        <Col lg={2} md={3} className="d-none d-md-block mb-3">
          <Card
            className="bg-white shadow-sm border-0 p-0 py-2 custom-sticky-top"
            style={{ top: "5rem" }}
          >
            {renderFilterNav()}
          </Card>
        </Col>

        <Col lg={10} md={9} sm={12} className="col-12">
      <Card className="mb-3 shadow-sm border-0">
        <Card.Header className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2 px-4 py-3 border-0">
          <div className="d-flex flex-column">
          <h6 className="mb-0 fw-semibold">Delegations</h6>
          <p className="text-muted small mb-0">
            {loading
              ? "Loading…"
              : `Showing ${filteredDelegations.length} of ${delegations.length} delegations`}
          </p>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            {hasActiveFilters ? (
              <Button
                variant="light-solid"
                size="sm"
                onClick={clearFilters}
                className="d-flex align-items-center"
              >
                <i className="bi bi-x-lg" />
                <span className="d-none d-md-inline-block ms-1">clear</span>
              </Button>
            ) : null}
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          + Delegate Task
        </Button>
          </div>
        </Card.Header>
      <Card.Body className="p-0">

      <Table hover responsive className="delegations-manager-table mb-0">
        <thead>
          <tr className="custom-header-bg">
            <th>User</th>
            <th>Role</th>
            <th> Assigned Task</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Status</th>
            <th style={{ width: 120 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr className="delegation-empty-placeholder">
              <td colSpan={TABLE_COL_COUNT} className="text-center py-4 text-muted">
                Loading…
              </td>
            </tr>
          ) : delegations.length === 0 ? (
            <tr className="delegation-empty-placeholder">
              <td colSpan={TABLE_COL_COUNT} className="text-center py-5">
                <div className="text-muted">
                  <i className="bi bi-person-lines-fill fs-1 mb-3 d-block" aria-hidden="true" />
                  <h5 className="mb-2 text-secondary">No delegations yet</h5>
                  <p className="mb-0 fw-normal small">
                    Use &quot;Delegate task&quot; to assign time-bound access to onboarding, conferences, or resources.
                  </p>
                </div>
              </td>
            </tr>
          ) : filteredDelegations.length === 0 ? (
            <tr className="delegation-empty-placeholder">
              <td colSpan={TABLE_COL_COUNT} className="text-center py-5">
                <div className="text-muted">
                  <h6 className="mb-2 text-muted">No delegations match these filters</h6>
                  <p className="mb-0 small">
                    Try a different role or assigned task, or clear filters to see all delegations.
                  </p>
                </div>
              </td>
            </tr>
          ) : (
            <>
              <tr className="delegation-section-header">
                <td
                  colSpan={TABLE_COL_COUNT}
                  className="custom-header-bg fw-semibold small py-2 px-3"
                >
                  Active &amp; upcoming
                </td>
              </tr>
              {activeSectionRows.length === 0 ? (
                <tr className="delegation-empty-placeholder">
                  <td colSpan={TABLE_COL_COUNT} className="text-center py-5">
                    <div>
                      <span className="d-inline-flex justify-content-center align-items-center bg-light rounded-3 p-2"><CalendarFold className="lucide-icon-lg"/></span>
                      <h6 className="mb-2 mt-2 fw-semibold text-muted">No active or upcoming delegations</h6>
                      <p className="mb-0 text-muted fw-normal">
                        Scheduled and in-progress delegations will appear here. Create one with &quot;Delegate task&quot;.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                activeSectionRows.map(renderDelegationRow)
              )}
              <tr className=" delegation-section-header">
                <td
                  colSpan={TABLE_COL_COUNT}
                  className="custom-header-bg fw-semibold small py-2 px-3"
                >
                  Expired
                </td>
              </tr>
              {expiredSectionRows.length === 0 ? (
                <tr className="delegation-empty-placeholder">
                  <td colSpan={TABLE_COL_COUNT} className="text-center py-5">
                    <div>
                      <span className="d-inline-flex justify-content-center align-items-center bg-light rounded-3 p-2"><CalendarX className="lucide-icon-lg"/></span>
                      <h6 className="mb-2 mt-2 fw-semibold text-muted">No expired delegations</h6>
                      <p className="mb-0 text-muted fw-normal">
                        Delegations past their end date are listed here for reference.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                expiredSectionRows.map(renderDelegationRow)
              )}
            </>
          )}
        </tbody>
      </Table>
      </Card.Body>
      </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-0 px-4 py-3">
          <div className="d-flex w-50 flex-column justify-content-start align-items-start">
            <Modal.Title>Delegate Task</Modal.Title>
            <div className="text-hl"></div>
          </div>
        </Modal.Header>
        <Modal.Body className="px-4">
          <Form>
            <Row>
              <Col md={6}>
            <Form.Group className="mb-3">
              <ExpandableUserSelector
                availableUsers={selectableUsers}
                selectedUsers={form.delegateeId ? [form.delegateeId] : []}
                onSelectionChange={(ids) =>
                  setForm((p) => ({
                    ...p,
                    delegateeId: ids.length ? ids[ids.length - 1] : "",
                  }))
                }
                helperText="Select the user who will receive delegated access."
              />
            </Form.Group>
            </Col>
            <Col md={6}>
            <Form.Group className="mb-3">
              <FloatingLabel controlId="floatingSelect" label="Task/module">
              <Form.Select
                value={form.moduleKey}
                onChange={(e) => setForm((p) => ({ ...p, moduleKey: e.target.value }))}
              >
                <option value="onboarding">Onboarding management</option>
                <option value="conferenceScheduler">Conference scheduling</option>
                <option value="resources">Resources</option>
              </Form.Select>
              </FloatingLabel>
            </Form.Group>
            </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <FloatingLabel controlId="floatingDate" label="Start date">
                  <Form.Control
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                  />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <FloatingLabel controlId="floatingDate" label="End date">
                  <Form.Control
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
                  />
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="px-4 border-0">
          <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={submit}>
            Create delegation
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

